import { useCallback } from 'react';
import { useDeliveryProofStore } from '@/features/delivery-proof/stores/deliveryProofStore';
import {
  useCreateDeliveryProof,
  useUpdateWarranties,
  useUpdateElements,
  useUpdateVehicleInfo,
  useSendDeliveryProof,
} from '@/features/delivery-proof/api/deliveryProofApi';
import type { DeliveryProofResponseDto } from '@/features/delivery-proof/types/delivery-proof.types';

interface UseDeliveryProofFormOptions {
  onComplete?: () => void;
}

/**
 * Hook principal pour gérer le formulaire de création d'attestation
 * Centralise toute la logique métier du workflow
 *
 * Navigation entre steps = locale (pas d'appels API)
 * "Prévisualiser" (step 4) = chaîne tous les appels API puis affiche la preview
 */
export const useDeliveryProofForm = (options?: UseDeliveryProofFormOptions) => {
  const store = useDeliveryProofStore();

  const createMutation = useCreateDeliveryProof();
  const updateWarrantiesMutation = useUpdateWarranties();
  const updateElementsMutation = useUpdateElements();
  const updateVehicleInfoMutation = useUpdateVehicleInfo();
  const sendMutation = useSendDeliveryProof();

  // Step 1: Créer l'attestation (ou récupérer existante)
  // Accepte soit contractNum (contrat finalisé) soit draftUuid (brouillon contrat)
  const createDeliveryProof = useCallback(
    async (
      source: { contractNum: string } | { draftUuid: string },
      dpNumber?: string,
      societyId?: string,
      userId?: string,
    ) => {
      const result = await createMutation.mutateAsync({
        ...source,
        dpNumber,
        societyId,
        userId,
      });

      store.setDeliveryProofId(result.id);

      const contractNum = 'contractNum' in source ? source.contractNum : '';
      // Mapper les response DTOs vers les item DTOs (supprimer id et champs extra)
      store.updateFormData({
        contractNum: result.contractNum || contractNum,
        draftUuid: result.draftUuid || ('draftUuid' in source ? source.draftUuid : undefined),
        dpNumber: result.dpNumber || dpNumber,
        societyId: result.societyId || societyId,
        userId,
        warranties: (result.warranties || []).map((w) => ({
          warrantyId: w.warrantyId ? Number(w.warrantyId) : undefined,
          isAccepted: Boolean(w.isAccepted),
          isExternal: Boolean(w.isExternal),
          externalName: w.externalName || undefined,
          externalKmLimit: w.externalKmLimit || undefined,
          externalFinancialCap: w.externalFinancialCap || undefined,
          externalDuration: w.externalDuration || undefined,
          externalPieces: w.externalPieces || undefined,
          externalPrice: w.externalPrice || undefined,
        })),
        hidePrices: Boolean(result.warranties?.some(w => w.hidePrices)),
        elements: (result.elements || []).map((e) => ({
          type: e.type,
          label: e.label,
          date: e.date,
          note: e.note,
          fileUrl: e.fileUrl,
          filePages: e.filePages,
          originalFileUrl: e.originalFileUrl,
        })),
        vehicleInfo: (result.vehicleInfo || []).map((v) => ({
          type: v.type,
          value: v.value,
          fileUrl: v.fileUrl,
          filePages: v.filePages,
          originalFileUrl: v.originalFileUrl,
          orderIndex: v.orderIndex,
        })),
      });
      // Chargement initial ≠ modification utilisateur
      store.markClean();

      return result;
    },
    [createMutation, store]
  );

  // Charger une attestation existante (reprise de brouillon DP existant)
  const loadDeliveryProof = useCallback((dp: DeliveryProofResponseDto) => {
    const { setDeliveryProofId, updateFormData, setStep, markClean } = useDeliveryProofStore.getState();
    setDeliveryProofId(dp.id);
    updateFormData({
      contractNum: dp.contractNum || '',
      draftUuid: dp.draftUuid || undefined,
      dpNumber: dp.dpNumber,
      societyId: dp.societyId,
      warranties: (dp.warranties || []).map((w) => ({
        warrantyId: w.warrantyId ? Number(w.warrantyId) : undefined,
        isAccepted: Boolean(w.isAccepted),
        isExternal: Boolean(w.isExternal),
        externalName: w.externalName || undefined,
        externalKmLimit: w.externalKmLimit || undefined,
        externalFinancialCap: w.externalFinancialCap || undefined,
        externalDuration: w.externalDuration || undefined,
        externalPieces: w.externalPieces || undefined,
        externalPrice: w.externalPrice || undefined,
      })),
      hidePrices: Boolean(dp.warranties?.some(w => w.hidePrices)),
      elements: (dp.elements || []).map((e) => ({
        type: e.type,
        label: e.label,
        date: e.date,
        note: e.note,
        fileUrl: e.fileUrl,
        filePages: e.filePages,
        originalFileUrl: e.originalFileUrl,
      })),
      vehicleInfo: (dp.vehicleInfo || []).map((v) => ({
        type: v.type,
        value: v.value,
        fileUrl: v.fileUrl,
        filePages: v.filePages,
        originalFileUrl: v.originalFileUrl,
        orderIndex: v.orderIndex,
      })),
    });
    setStep(dp.currentStep || 1);
    markClean();
  }, []);

  // Chaîner tous les appels API pour sauvegarder l'attestation complète
  // Appelé avant preview (step 4) et avant envoi
  const submitAll = useCallback(async () => {
    const { deliveryProofId, formData, currentStep } = useDeliveryProofStore.getState();
    if (!deliveryProofId) throw new Error('No delivery proof ID');

    const { warranties, hidePrices, elements, vehicleInfo } = formData;

    // Chaîner les mises à jour séquentiellement
    await updateWarrantiesMutation.mutateAsync({
      id: deliveryProofId,
      data: { warranties, hidePrices, currentStep },
    });

    await updateElementsMutation.mutateAsync({
      id: deliveryProofId,
      data: { elements, currentStep },
    });

    await updateVehicleInfoMutation.mutateAsync({
      id: deliveryProofId,
      data: { vehicleInfo, currentStep },
    });

    useDeliveryProofStore.getState().markClean();
  }, [updateWarrantiesMutation, updateElementsMutation, updateVehicleInfoMutation]);

  // Générer l'attestation avec le type de signature choisi
  const sendDeliveryProof = useCallback(async (signatureType: 1 | 2, email?: string, phone?: string) => {
    const { deliveryProofId, reset } = useDeliveryProofStore.getState();
    if (!deliveryProofId) throw new Error('No delivery proof ID');

    const result = await sendMutation.mutateAsync({
      id: deliveryProofId,
      data: { signatureType, email, phone },
    });
    reset();
    options?.onComplete?.();
    return result;
  }, [sendMutation, options]);

  // Navigation simple entre les steps (pas d'auto-save, comme le wizard contrat)
  const goToStep = useCallback((step: number) => {
    const { setStep } = useDeliveryProofStore.getState();
    setStep(step);
  }, []);

  const nextStep = useCallback(() => {
    useDeliveryProofStore.getState().nextStep();
  }, []);

  const previousStep = useCallback(() => {
    const { currentStep } = useDeliveryProofStore.getState();
    goToStep(currentStep - 1);
  }, [goToStep]);

  return {
    // État
    currentStep: store.currentStep,
    deliveryProofId: store.deliveryProofId,
    formData: store.formData,
    isDirty: store.isDirty,

    // Actions
    createDeliveryProof,
    loadDeliveryProof,
    submitAll,
    sendDeliveryProof,
    goToStep,
    nextStep,
    previousStep,
    updateFormData: store.updateFormData,
    reset: store.reset,

    // États de chargement
    isCreating: createMutation.isLoading,
    isSubmittingAll:
      updateWarrantiesMutation.isLoading ||
      updateElementsMutation.isLoading ||
      updateVehicleInfoMutation.isLoading,
    isSending: sendMutation.isLoading,
  };
};
